import React from 'react';
import { DataTable, DataTableColumn, IdLink, ServiceAreaPill, displayValue } from '@/components/ui';
import { FleetStatus, Vehicle } from '../../../types/Vehicle';
import { fleetStatusLabel } from '../utils/equipmentDisplay';
import { unitRoadTypeLabel } from '../utils/roadType';
import EquipmentStatusPill, { AvailabilityPill } from './EquipmentStatusPill';
import EquipmentFlags from './EquipmentFlags';

interface EquipmentTableProps {
  vehicles: Vehicle[];
  loading?: boolean;
  selectedIds: Set<number>;
  onToggleRow: (id: number) => void;
  onToggleAll: (checked: boolean) => void;
  onSelectVehicle: (vehicle: Vehicle) => void;
  sortBy?: string | null;
  sortDir?: 'asc' | 'desc';
  onSort?: (key: string, dir: 'asc' | 'desc') => void;
}

function fleetTone(status: string | undefined): 'success' | 'danger' | 'neutral' {
  if (status === FleetStatus.Active) return 'success';
  if (status === FleetStatus.Retired || status === FleetStatus.Inactive) return 'danger';
  return 'neutral';
}

/** A unit's service area (a unit belongs to exactly one, EQ-03). */
export function unitServiceArea(vehicle: Vehicle) {
  return vehicle.vehicleServiceAreas?.[0]?.serviceArea ?? null;
}

/**
 * Equipment list columns: checkbox, photo, Unit #, Description, Vehicle Classification, Type
 * (On-Road / Off-Road), Make, Model, Year, Service Area, Foreman Area, Flags, both statuses.
 */
const EquipmentTable: React.FC<EquipmentTableProps> = ({
  vehicles,
  loading,
  selectedIds,
  onToggleRow,
  onToggleAll,
  onSelectVehicle,
  sortBy,
  sortDir,
  onSort
}) => {
  const allSelected = vehicles.length > 0 && vehicles.every((v) => selectedIds.has(v.id));

  const columns: DataTableColumn<Vehicle>[] = [
    {
      key: 'select',
      header: (
        <input
          type="checkbox"
          checked={allSelected}
          onChange={(e) => onToggleAll(e.target.checked)}
          onClick={(e) => e.stopPropagation()}
          aria-label="Select all units on this page"
        />
      ),
      mobile: 'hidden',
      width: '2.5rem',
      cell: (v) => (
        <input
          type="checkbox"
          checked={selectedIds.has(v.id)}
          onChange={() => onToggleRow(v.id)}
          onClick={(e) => e.stopPropagation()}
          aria-label={`Select ${v.unitNumber}`}
        />
      )
    },
    {
      key: 'photo',
      header: '',
      mobile: 'hidden',
      width: '3rem',
      cell: (v) => (
        <div className="tw-h-10 tw-w-10 tw-overflow-hidden tw-rounded tw-bg-surface-alt">
          {v.imageUrl ? (
            <img src={v.imageUrl} alt="" className="tw-h-full tw-w-full tw-object-cover" />
          ) : (
            <div className="tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center tw-text-content-muted">
              <i className="bi bi-truck" aria-hidden="true" />
            </div>
          )}
        </div>
      )
    },
    {
      key: 'unitNumber',
      header: 'Unit #',
      sortable: true,
      mobile: 'title',
      cell: (v) => <IdLink to={`/equipment/${v.id}`} onClick={(e) => e.stopPropagation()}>{v.unitNumber}</IdLink>
    },
    {
      key: 'description',
      header: 'Description',
      sortable: true,
      mobile: 'subtitle',
      cell: (v) => displayValue(v.description)
    },
    {
      key: 'vehicleClassification',
      header: 'Vehicle Classification',
      sortable: true,
      cell: (v) => displayValue(v.vehicleClassification)
    },
    {
      key: 'vehicleType',
      header: 'Type',
      sortable: true,
      // "On-Road" never breaks at its hyphen.
      cell: (v) => <span className="tw-whitespace-nowrap">{displayValue(unitRoadTypeLabel(v))}</span>
    },
    {
      key: 'make',
      header: 'Make',
      cell: (v) => displayValue(v.make)
    },
    {
      key: 'model',
      header: 'Model',
      cell: (v) => displayValue(v.model)
    },
    {
      key: 'year',
      header: 'Year',
      cell: (v) => displayValue(v.year != null ? String(v.year) : null)
    },
    {
      key: 'serviceArea',
      header: 'Service Area',
      // Compact "SA16": this table has many columns (FMT-19).
      cell: (v) => <ServiceAreaPill value={unitServiceArea(v)} compact />
    },
    {
      key: 'foremanArea',
      header: 'Foreman Area',
      cell: (v) => displayValue(v.foremanArea?.name)
    },
    {
      key: 'flags',
      header: 'Flags',
      // A tap on a flag opens its tooltip (TCH-03), not the unit.
      cell: (v) =>
        v.flags && v.flags.length > 0 ? (
          <div onClick={(e) => e.stopPropagation()} role="presentation">
            <EquipmentFlags flags={v.flags} wrap={false} />
          </div>
        ) : null
    },
    {
      key: 'status',
      header: 'Equipment Status',
      sortable: true,
      cell: (v) => (
        <EquipmentStatusPill label={fleetStatusLabel(v.status)} tone={fleetTone(v.status)} />
      )
    },
    {
      key: 'availability',
      header: 'Availability',
      mobile: 'trailing',
      cell: (v) => <AvailabilityPill vehicle={v} />
    }
  ];

  return (
    <DataTable<Vehicle>
      columns={columns}
      data={vehicles}
      rowKey={(v) => v.id}
      loading={loading}
      onRowClick={onSelectVehicle}
      framed
      sortBy={sortBy}
      sortDir={sortDir}
      onSort={onSort}
      emptyMessage="No units match these filters."
    />
  );
};

export default EquipmentTable;
